import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import SEO from "@/components/seo";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Reveal } from "@/components/reveal";
import { apiFetch } from "@/lib/api";
import { useState, useRef } from "react";

const formSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Valid email is required"),
  phone: z.string().min(1, "Phone number is required"),
  serviceNeeded: z.string().min(1, "Please select a service"),
  petCount: z.string().optional(),
  prospectiveStartDate: z.string().min(1, "Start date is required"),
  prospectiveEndDate: z.string().optional(),
  crossStreets: z.string().optional(),
  frequency: z.string().optional(),
  message: z.string().optional(),
});

export default function ContactUs() {
  const { toast } = useToast();
  const formLoadTime = useRef(Date.now());
  const [honeypot, setHoneypot] = useState("");

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      serviceNeeded: "",
      petCount: "",
      prospectiveStartDate: "",
      prospectiveEndDate: "",
      crossStreets: "",
      frequency: "",
      message: "",
    },
  });

  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setSubmitting(true);
    try {
      await apiFetch("/contact", {
        method: "POST",
        body: JSON.stringify({
          ...values,
          _website: honeypot,
          _ts: formLoadTime.current,
        }),
      });
      toast({
        title: "Message Sent!",
        description: "We'll get back to you and your furry friend shortly.",
      });
      form.reset();
      setHoneypot("");
      formLoadTime.current = Date.now();
    } catch {
      toast({
        title: "Something went wrong",
        description: "Please try again or call us at (850) 728-6220.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="py-16 md:py-24">
      <SEO
        title="Contact Us | Get in Touch"
        description="Contact Tallahassee Lassie Pet Care for professional dog walking and pet sitting services in Tallahassee, FL. Call (850) 728-6220 or fill out our contact form. We'd love to hear from you!"
        path="/contact-us"
      />
      <div className="container mx-auto px-4 max-w-6xl">
        <Reveal>
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-foreground mb-6">Contact Us</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Get in touch with us to learn more about our services or to set up a Meet & Greet.
            </p>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-3 gap-12">
          <Reveal direction="left" className="lg:col-span-1 space-y-8 order-2 lg:order-1">
            <div className="bg-primary/5 p-8 border border-primary/10" style={{ borderRadius: "24px 0 24px 0" }}>
              <h3 className="text-2xl font-serif font-bold mb-6">Contact Info</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary/20 text-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail size={20} />
                  </div>
                  <div>
                    <p className="font-bold text-sm uppercase tracking-wider text-muted-foreground mb-1">Email Us</p>
                    <a href="mailto:carla@tallylassie.com" className="text-lg font-medium hover:text-primary transition-colors">
                      carla@tallylassie.com
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-secondary/20 text-secondary rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone size={20} />
                  </div>
                  <div>
                    <p className="font-bold text-sm uppercase tracking-wider text-muted-foreground mb-1">Call/Text</p>
                    <a href="tel:+18507286220" className="text-lg font-medium hover:text-secondary transition-colors">
                      (850) 728-6220
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-muted text-muted-foreground rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <p className="font-bold text-sm uppercase tracking-wider text-muted-foreground mb-1">Service Area</p>
                    <p className="text-lg font-medium">
                      Tallahassee, FL
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-secondary text-white p-8" style={{ borderRadius: "0 24px 0 24px" }}>
              <h3 className="text-xl font-serif font-bold mb-3">New Client?</h3>
              <p className="text-white/80 mb-6">Create a free account to get started!</p>
              <Button asChild variant="secondary" className="w-full bg-white text-secondary hover:bg-white/90 mb-4">
                <a href="https://www.timetopet.com/portal/tallylassiepets/create-account" target="_blank" rel="noopener noreferrer">
                  Create Account
                </a>
              </Button>
              <h3 className="text-xl font-serif font-bold mb-3 mt-6">Current Client?</h3>
              <p className="text-white/80 mb-6">Access your account to request visits, update pet info, or pay invoices.</p>
              <Button asChild variant="secondary" className="w-full bg-white text-secondary hover:bg-white/90">
                <a href="https://www.timetopet.com/portal/tallylassiepets" target="_blank" rel="noopener noreferrer">
                  Client Login
                </a>
              </Button>
            </div>
          </Reveal>

          <Reveal direction="right" className="lg:col-span-2 order-1 lg:order-2">
            <div className="bg-card p-8 md:p-10 border shadow-sm" style={{ borderRadius: "32px 0 32px 0" }}>
              <h2 className="text-3xl font-serif font-bold mb-2">Get in Touch</h2>
              <p className="text-sm text-muted-foreground mb-8">Fields marked with * are required.</p>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  {/* Name */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>First Name *</FormLabel>
                          <FormControl>
                            <Input placeholder="First name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Last Name *</FormLabel>
                          <FormControl>
                            <Input placeholder="Last name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Contact */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email *</FormLabel>
                          <FormControl>
                            <Input placeholder="you@example.com" type="email" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone *</FormLabel>
                          <FormControl>
                            <Input placeholder="(850) 000-0000" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Service */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="serviceNeeded"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Service Needed *</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a service" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="vip">VIP Recurring Dog Walks</SelectItem>
                              <SelectItem value="pupventures">Pupventures</SelectItem>
                              <SelectItem value="bed-and-breakfast">Bed & Breakfast</SelectItem>
                              <SelectItem value="occasional">Occasional Dog Walking</SelectItem>
                              <SelectItem value="petsitting">Pet Sitting Visits</SelectItem>
                              <SelectItem value="pettaxi">Pet Taxi</SelectItem>
                              <SelectItem value="other">Other / Not sure</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="petCount"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>How Many Pets?</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="1">1</SelectItem>
                              <SelectItem value="2">2</SelectItem>
                              <SelectItem value="3">3</SelectItem>
                              <SelectItem value="4+">4 or more</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Service Details */}
                  <div className="pt-2">
                    <h3 className="text-lg font-serif font-bold mb-1">Service Details</h3>
                    <p className="text-sm text-muted-foreground mb-5">
                      Sharing a few details up front helps us respond with specifics.
                    </p>

                    <div className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <FormField
                          control={form.control}
                          name="prospectiveStartDate"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Prospective Start Date *</FormLabel>
                              <FormControl>
                                <Input type="date" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="prospectiveEndDate"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>End Date <span className="text-muted-foreground font-normal">(if applicable)</span></FormLabel>
                              <FormControl>
                                <Input type="date" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <FormField
                          control={form.control}
                          name="crossStreets"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Nearest Cross Streets</FormLabel>
                              <FormControl>
                                <Input placeholder="e.g., Thomasville Rd & Betton Rd" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="frequency"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>How Often</FormLabel>
                              <Select onValueChange={field.onChange} defaultValue={field.value} value={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select cadence" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="one-time">One-time</SelectItem>
                                  <SelectItem value="occasional">Occasional / as-needed</SelectItem>
                                  <SelectItem value="weekly">Weekly</SelectItem>
                                  <SelectItem value="2-3-week">2–3 times a week</SelectItem>
                                  <SelectItem value="weekdays">Weekdays (Mon–Fri)</SelectItem>
                                  <SelectItem value="daily">Daily</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="message"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Additional Information to Share</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Tell us anything helpful — your pet's name, temperament, routine, specific needs, or any questions you have."
                                className="resize-none min-h-[120px]"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <input
                    type="text"
                    name="_website"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    autoComplete="off"
                    tabIndex={-1}
                    aria-hidden="true"
                    style={{ position: "absolute", left: "-9999px", width: 0, height: 0, opacity: 0, overflow: "hidden", pointerEvents: "none" }}
                  />

                  <Button type="submit" size="lg" className="w-full text-lg h-14 flex items-center gap-2" disabled={submitting}>
                    <Send size={18} /> {submitting ? "Sending..." : "Send"}
                  </Button>
                  
                  <p className="text-sm text-muted-foreground text-center">
                    We look forward to hearing from you and meeting your pet care needs.
                  </p>
                </form>
              </Form>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
